import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import { Instruction, RecipeFormData } from "../pages/Kitchen/Recipes/types";

interface ListParams {
  searchQuery?: string;
  filters?: Array<{
    id: string;
    title: string;
    field: string;
    operator: string;
    value: any;
  }>;
  startDate?: number;
  endDate?: number;
  dateField?: string;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  pageIndex: number;
  pageSize: number;
  category?: string;
}

interface ListResponse {
  data: any[];
  metadata: {
    total: {
      value: number;
    };
    took: number;
    timed_out: boolean;
    max_score: number | null;
  };
}

const transformRecipeForAPI = (recipe: any): any => {
  // Calculate total preparation and cooking times from instructions
  const totalTimes = recipe.instructions.reduce(
    (acc: any, instruction: any) => ({
      totalPrepTime: acc.totalPrepTime + (instruction.preparation_time || 0),
      totalCookTime: acc.totalCookTime + (instruction.cooking_time || 0),
    }),
    { totalPrepTime: 0, totalCookTime: 0 }
  );

  return {
    ...recipe,
    total_preparing_time: totalTimes.totalPrepTime,
    total_cooking_time: totalTimes.totalCookTime,
    instructions: recipe.instructions.map((instruction: any) => ({
      content: instruction.content,
      preparation_time: instruction.preparation_time,
      preparation_time_unit: instruction.preparation_time_unit,
      cooking_time: instruction.cooking_time,
      cooking_time_unit: instruction.cooking_time_unit,
      temperature: instruction.temperature,
      temperature_unit: instruction.temperature_unit,
    })),
  };
};

const transformRecipeFromAPI = (recipe: any): RecipeFormData => {
  return {
    ...recipe,
    is_active: true,
    notes: recipe.notes || "",
    ingredients: recipe.ingredients.map((ing: any) => ({
      ...ing,
      parent_unit: ing.unit,
      notes: ing.notes || "",
    })),
    instructions: recipe.instructions.map((instruction: any) => ({
      content: instruction.content,
      preparation_time: instruction.preparation_time || 0,
      preparation_time_unit: instruction.preparation_time_unit || "min",
      cooking_time: instruction.cooking_time || 0,
      cooking_time_unit: instruction.cooking_time_unit || "min",
      temperature: instruction.temperature || 0,
      temperature_unit: instruction.temperature_unit || "C",
    })),
  };
};

class KitchenService {
  async listRecipes(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post("/business-admin/recipe/list", {
      ...params,
    });
    return response.data.data;
  }

  async getRecipe(id: string): Promise<AxiosResponse<RecipeFormData>> {
    const response = await axiosInstance.post("/business-admin/recipe/get", {
      _id: id,
    });
    return {
      ...response,
      data: transformRecipeFromAPI(response.data.data),
    };
  }

  async createRecipe(data: RecipeFormData): Promise<AxiosResponse<any>> {
    const transformedData = transformRecipeForAPI(data);
    const response = await axiosInstance.post(
      "/business-admin/recipe/create",
      transformedData
    );
    return response;
  }

  async updateRecipe(data: {
    _id: string;
    name: string;
    description: string;
    category: string;
    servings: number;
    yield_quantity: number;
    yield_unit: string;
    total_preparing_time: number;
    total_cooking_time: number;
    instructions: {
      content: string;
      preparation_time: number;
      preparation_time_unit: string;
      cooking_time: number;
      cooking_time_unit: string;
      temperature: number;
      temperature_unit: string;
    }[];
    ingredients: Array<{
      item_id: string;
      item_type: "ingredient" | "supply";
      quantity: number;
      unit: string;
      parent_unit: string;
      notes?: string;
      ingredient_id: string;
      selected_batch_id?: string;
      brand_name: string;
    }>;
    tags: string[];
    is_active: boolean;
    notes: string;
  }): Promise<AxiosResponse<any>> {
    const transformedData = transformRecipeForAPI(data);
    const response = await axiosInstance.post(
      "/business-admin/recipe/update",
      transformedData
    );
    return response;
  }

  async deleteRecipe(id: string): Promise<AxiosResponse<any>> {
    const response = await axiosInstance.post("/business-admin/recipe/delete", {
      _id: id,
    });
    return response;
  }

  async listProcessedItems(params: ListParams): Promise<ListResponse> {
    const response = await axiosInstance.post(
      "/business-admin/processed-items/list",
      {
        ...params,
      }
    );
    return response.data.data;
  }
}

export const kitchenService = new KitchenService();
